/**
 * Lightweight console ring buffer for the MIRA feedback widget.
 *
 * Patches `console.{log,info,warn,error,debug}` once at module load
 * (idempotent) and keeps the last N formatted lines in a ring buffer
 * so we can ship them as telemetry when a user files a ticket.
 *
 * Memory cap: 50 lines × ~250 chars ≈ 12 KB worst case.
 */
const MAX_LINES = 50;
const buffer = [];
let _patched = false;

function _fmt(args) {
  try {
    return args
      .map((a) => {
        if (a == null) return String(a);
        if (typeof a === "string") return a;
        if (a instanceof Error) return `${a.name}: ${a.message}`;
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      })
      .join(" ")
      .slice(0, 500);
  } catch {
    return "[unserializable]";
  }
}

function _push(level, args) {
  const ts = new Date().toISOString().slice(11, 19);
  buffer.push(`[${ts}] [${level}] ${_fmt(args)}`);
  if (buffer.length > MAX_LINES) buffer.shift();
}

export function startConsoleCapture() {
  if (_patched || typeof window === "undefined") return;
  _patched = true;
  ["log", "info", "warn", "error", "debug"].forEach((lvl) => {
    const original = console[lvl];
    if (!original) return;
    console[lvl] = function patched(...args) {
      _push(lvl, args);
      return original.apply(console, args);
    };
  });
  // Catch uncaught errors too.
  window.addEventListener("error", (e) => {
    _push("error", [`uncaught: ${e?.message || e}`, e?.filename || "", e?.lineno || ""]);
  });
  window.addEventListener("unhandledrejection", (e) => {
    _push("error", [`unhandled-promise: ${e?.reason?.message || e?.reason || ""}`]);
  });
}

export function getConsoleTail() {
  return [...buffer];
}
